import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MaterialTable from 'material-table';
import { Divider, List } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { gql } from '@apollo/client';

import { mutate, query } from '../../../shared/apollo/ApolloClient';
import SnackConfig from '../../../shared/config/snackConfig';
import DropdownActions from '../../../shared/components/DropdownActions';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import OnlineMetricsJob, { MetricsListData, MetricsTableRowData } from './OnlineMetricsJob';
import { Job } from '../../../shared/services/job';

const useStyles = makeStyles({
  title: {
    textAlign: 'center',
  },
  icons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

class Jobs {
  jobs: Job[];
  pendingJobs: Record<number, number>;
  finishedJobs: Record<number, number>;
  // Assumes the input list is sorted descending w.r.t. created_at
  constructor(jobs: Job[]) {
    this.jobs = jobs;
    this.pendingJobs = {};
    this.finishedJobs = {};
    this.jobs.forEach((job, index) => {
      const { status, id } = job;
      if (status === 'QUEUED' || status === 'IN_PROGRESS') {
        this.pendingJobs[id] = index;
      } else {
        this.finishedJobs[id] = index;
      }
    });
  }

  getJobs() {
    return this.jobs;
  }
}

// Merge ...newJobs and jobs and de-dup. Both ...newJobs and jobs must be in chronologically decreasing order
export const insertJob = (jobs: Job[], ...newJobs: Job[]): Job[] => {
  // Create an index of existing jobs
  const jobsMap: Record<number, number> = {};
  jobs.forEach((job, index) => {
    jobsMap[job.id] = index;
  });

  // Create a copy of existing jobs
  const mergedJobs = [...jobs];

  const freshNewJobs: Job[] = [];
  // For each newJob which already existed in the old jobs list, just replace with the latest
  newJobs.forEach((newJob) => {
    if (jobsMap[newJob.id]) {
      mergedJobs[jobsMap[newJob.id]] = newJob;
    } else {
      freshNewJobs.push(newJob);
    }
  });

  // For the remaining fresh new jobs, insert them chronologically
  let i = 0;
  freshNewJobs.forEach((newJob) => {
    for (; i < mergedJobs.length; i += 1) {
      if (moment(mergedJobs[i].createdAt).isSameOrBefore(moment(newJob.createdAt))) {
        break;
      }
    }
    mergedJobs.splice(i, 0, newJob);
  });

  return mergedJobs;
};

const removeJobs = (jobs: Job[], ids: number[]): Job[] => {
  // Create an index of jobs to be removed
  const jobsToRemoveMap: Record<number, boolean> = {};
  ids.forEach((id) => {
    jobsToRemoveMap[id] = true;
  });

  const newJobs: Job[] = [];
  jobs.forEach((job) => {
    if (!Object.prototype.hasOwnProperty.call(jobsToRemoveMap, job.id)) {
      newJobs.push(job);
    }
  });
  return newJobs;
};

const updateJob = (jobs: Job[], id: number, newJob: Job): Job[] => jobs.map((job) => (job.id !== id ? job : newJob));

const JOBS_QUERY = gql`
  query jobs($types: [String!]!, $limit: Int!, $ignoreBot: Boolean!) {
    jobs(types: $types, limit: $limit, ignoreBot: $ignoreBot) {
      createdAt
      startedAt
      completedAt
      options
      id
      status
      results
      type
      rating
      parentId
    }
  }
`;

const JOB_QUERY = gql`
  query job($id: Int32!) {
    job(id: $id) {
      createdAt
      startedAt
      completedAt
      options
      id
      status
      results
      type
      rating
      parentId
    }
  }
`;

interface RemoveJobsResp {
  deleteJobs: {
    ids: number[];
  };
}

const REMOVE_JOBS = gql`
  mutation deleteJobs($jobIds: [Int32!]!) {
    deleteJobs(jobIds: $jobIds) {
      ids
    }
  }
`;

const MAX_JOBS = 50;

interface OnlineMetricsJobResultsProps {
  jobTypes: string[];
  jobId?: number;
  transformResults(job: Job): MetricsListData;
  columns: Record<string, unknown>[];
  actions?: MaterialTable<MetricsTableRowData>['props']['actions'];
}

const OnlineMetricsJobResults: React.FC<OnlineMetricsJobResultsProps> = ({
  jobTypes,
  jobId,
  transformResults,
  columns,
  actions = [],
}: OnlineMetricsJobResultsProps) => {
  const classes = useStyles();

  const [jobs, setJobs] = useState<Jobs>(new Jobs([]));
  const [selectedJobIds, setSelectedJobIds] = useState<Record<number, boolean>>({});
  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));
  const [fetchingJobs, setFetchingJobs] = useState<boolean>(true);

  useEffect(() => {
    // Get top MAX_JOBS number of jobs
    void (async () => {
      try {
        const data = await query<Record<string, Job[]>>({
          query: JOBS_QUERY,
          variables: {
            types: jobTypes,
            limit: MAX_JOBS,
            ignoreBot: true,
          },
          fetchPolicy: 'network-only',
        });
        const { jobs: jobResults } = data!;
        setJobs((prevJobs) => {
          const currJobs = prevJobs.getJobs();
          const rawJobs = insertJob(currJobs, ...jobResults);
          return new Jobs(rawJobs.slice(0, MAX_JOBS));
        });
      } catch (error) {
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: JSON.stringify(error),
          })
        );
      }
      setFetchingJobs(false);
    })();
  }, [jobTypes]);

  const queryJob = async (id: number) => {
    // Append the job to jobs
    try {
      const data = await query<Record<string, Job>>({
        query: JOB_QUERY,
        variables: {
          id,
        },
      });
      const { job: jobResult } = data!;
      setJobs((prevJobs) => {
        const currJobs = prevJobs.getJobs();
        const rawJobs = insertJob(currJobs, jobResult);
        return new Jobs(rawJobs.slice(0, MAX_JOBS));
      });
    } catch (error) {
      setSnackConfig(
        new SnackConfig({
          open: true,
          message: JSON.stringify(error),
        })
      );
    }
  };

  useEffect(() => {
    if (jobId) {
      void queryJob(jobId);
    }
  }, [jobId]);

  const addJob = (job: Job) => {
    setJobs((prevJobs) => {
      const currJobs = prevJobs.getJobs();
      const rawJobs = insertJob(currJobs, job);
      return new Jobs(rawJobs.slice(0, MAX_JOBS));
    });
  };

  const selectCallbacks = {
    Add: (id: number) => {
      setSelectedJobIds((prevIds) => {
        const ids = { ...prevIds };
        ids[id] = true;
        return ids;
      });
    },
    Remove: (id: number) => {
      setSelectedJobIds((prevIds) => {
        const ids = { ...prevIds };
        delete ids[id];
        return ids;
      });
    },
  };

  const deleteJobs = async () => {
    setFetchingJobs(true);
    try {
      const jobIds = Object.keys(selectedJobIds).map((id) => Number(id));
      const data = await mutate<RemoveJobsResp>({
        mutation: REMOVE_JOBS,
        variables: {
          jobIds,
        },
      });
      const { ids: removedJobIds } = data!.deleteJobs;
      setJobs((prevJobs) => {
        const currJobs = prevJobs.getJobs();
        const rawJobs = removeJobs(currJobs, removedJobIds);
        return new Jobs(rawJobs.slice(0, MAX_JOBS));
      });
    } catch (error) {
      setSnackConfig(
        new SnackConfig({
          open: true,
          message: JSON.stringify(error),
        })
      );
    }
    setFetchingJobs(false);
  };

  const cloningSupportedJobTypes = new Set(['RELEVANCE_RANKING']);

  return (
    <div>
      <h2 className={classes.title}>Recent History</h2>
      <div className={classes.icons}>
        <DropdownActions
          aria-label="action"
          actions={[
            {
              title: 'Delete',
              disabled: Object.keys(selectedJobIds).length === 0,
              onClick: deleteJobs,
            },
          ]}
        >
          <FontAwesomeIcon icon={faCog} />
        </DropdownActions>
      </div>
      <SpinnerOverlay active={fetchingJobs}>
        <List>
          {jobs.getJobs().map((job) => (
            <div key={job.id}>
              <Divider />
              <OnlineMetricsJob
                job={job}
                transformResults={transformResults}
                columns={columns}
                actions={actions}
                updateJob={(id: number, changedJob: Job) => {
                  setJobs((prevJobs) => {
                    const currJobs = prevJobs.getJobs();
                    const rawJobs = updateJob(currJobs, id, changedJob);
                    return new Jobs(rawJobs.slice(0, MAX_JOBS));
                  });
                }}
                enableCloning={cloningSupportedJobTypes.has(job.type)}
                addJob={addJob}
                selectCallbacks={selectCallbacks}
              />
            </div>
          ))}
        </List>
      </SpinnerOverlay>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default OnlineMetricsJobResults;
